// src/components/modals/common/DeleteConfirmationModal.tsx
import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

interface DeleteConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    resourceType: string;  // e.g., "RAG Application" or "Knowledge Base"
    resourceName: string;  // The specific name of the resource being deleted
    isLoading?: boolean;
    error?: Error | null;  // New prop for error handling
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
                                                                             isOpen,
                                                                             onClose,
                                                                             onConfirm,
                                                                             resourceType,
                                                                             resourceName,
                                                                             isLoading = false,
                                                                             error = null
                                                                         }) => {
    const [confirmText, setConfirmText] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (confirmText.toLowerCase() === 'delete') {
            onConfirm();
        }
    };

    const getErrorMessage = (error: Error | string) => {
        const message = error instanceof Error ? error.message : error;
        if (message.includes('Cannot delete index as it is used in')) {
            const apps = message.split('applications:')[1].trim();
            return (
                <div>
                    <p className="mb-2">This knowledge base cannot be deleted because it is being used by the following applications:</p>
                    <ul className="mb-2 ps-4">
                        {apps.split(',').map((app, index) => (
                            <li key={index}>{app.trim()}</li>
                        ))}
                    </ul>
                    <p className="mb-0">Please remove the knowledge base from these applications before deleting.</p>
                </div>
            );
        }
        return message;
    };

    return (
        <Modal show={isOpen} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h5">Delete {resourceType}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {getErrorMessage(error)}
                    </Alert>
                )}

                <p className="mb-3">
                    Are you sure you want to delete <strong>{resourceName}</strong>? This action cannot be undone.
                    Please type <strong>delete</strong> to confirm.
                </p>
                <Form onSubmit={handleSubmit}>
                    <Form.Control
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Type 'delete' to confirm"
                        className="border mb-3"
                    />
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="light"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            type="submit"
                            disabled={confirmText.toLowerCase() !== 'delete' || isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"/>
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default DeleteConfirmationModal;